<template>
  <v-container>
    <v-row v-if="!selectedPlacementId">
      <v-col>
        <div class="d-flex ga-2 justify-end mt-6 mt-md-0 w-100">
          <v-btn
            color="primary"
            class="text-sm-body-2 mb-2 mb-sm-0 ml-sm-2"
            prepend-icon="mdi-plus"
            variant="flat"
            text="Add Placement"
            data-testid="add-placement-btn"
            @click="showAddPlacementDialog = true"
          />
        </div>
      </v-col>
      <v-col cols="12">
        <v-alert
          type="info"
          variant="tonal"
          class="mb-3"
          density="comfortable"
          data-testid="placements-helper"
        >
          Placements are the spots on your site where coupons appear. Create one
          for each page or section (for example, Parts coupons or Service
          coupons). Add coupons to control what shows there.
        </v-alert>
      </v-col>
      <v-col cols="12">
        <v-card class="data-list">
          <v-card-text>
            <v-data-table
              :headers="tableHeaders"
              :items="placementItems || []"
              :items-per-page="itemsPerPage"
              :loading="isTableLoading"
              height="400px"
              fixed-header
              hover
              :page="page"
              :items-per-page-options="[5, 8, 10]"
              :items-length="totalItems"
              :server-items-length="totalItems"
              must-sort
              item-value="id"
              data-testid="placements-table"
            >
              <template #no-data>
                <div class="text-center py-6 w-100">
                  <v-icon size="48" color="grey-lighten-1"
                    >mdi-view-grid-plus</v-icon
                  >
                  <p class="mt-2 text-grey">No placements yet</p>
                  <v-btn
                    color="primary"
                    class="mt-2"
                    data-testid="add-first-placement-btn"
                    @click="showAddPlacementDialog = true"
                  >
                    Add your first placement
                  </v-btn>
                </div>
              </template>
              <template v-slot:item="{ item }">
                <tr
                  @click="handleRowClick(item)"
                  class="cursor-pointer"
                  :data-testid="`placement-row-${item.id}`"
                >
                  <td :data-testid="`placement-name-${item.id}`">
                    {{ item.name }}
                  </td>
                  <td :data-testid="`placement-id-${item.id}`">
                    {{ item.id }}
                  </td>
                  <td :data-testid="`placement-coupons-${item.id}`">
                    {{ item.couponCount }}
                  </td>
                  <td :data-testid="`placement-status-${item.id}`">
                    <v-chip
                      :color="item.enabled ? 'success' : 'grey'"
                      size="small"
                      variant="flat"
                    >
                      {{ item.enabled ? 'On' : 'Off' }}
                    </v-chip>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>

  <!-- Add Placement Dialog -->
  <v-dialog
    v-model="showAddPlacementDialog"
    max-width="500px"
    data-testid="add-placement-dialog"
  >
    <v-card>
      <v-card-title>Add New Placement</v-card-title>
      <v-card-text>
        <v-text-field
          v-model="newPlacementId"
          label="Placement ID"
          hint="Lowercase letters, numbers, hyphens, and underscores. This becomes part of the embed code and can’t be changed later."
          persistent-hint
          placeholder="e.g., homepage_hero"
          data-testid="placement-id-input"
        ></v-text-field>
        <div
          class="text-caption text-medium-emphasis mt-1"
          data-testid="placement-id-helper"
        >
          Examples: homepage_hero, srp_sidebar
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          variant="text"
          @click="showAddPlacementDialog = false"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="addNewPlacement"
          :disabled="!newPlacementId || !!placementIdError"
          data-testid="add-placement-submit-btn"
        >
          Add Placement
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Snackbar -->
  <v-snackbar
    v-model="showSnackbar"
    :timeout="2000"
    :color="snackbarColor"
    data-testid="success-snackbar"
  >
    {{ snackbarText }}

    <template #actions>
      <v-btn icon="mdi-close" @click="showSnackbar = false"></v-btn>
    </template>
  </v-snackbar>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import type { IPlacements } from '@/stores/types'
import {
  formatPlacementIdToFriendlyName,
  getDefaultPlacementSettings,
} from '@/utils'
import { useProductSettingsStore } from '@/stores/productSettings'

const props = defineProps<{
  accountId: string
  placements: IPlacements
}>()

const emit = defineEmits<{
  'update:placementId': [placementId: string]
}>()

const productSettings = useProductSettingsStore()

const showAddPlacementDialog = ref(false)
const newPlacementId = ref('')

const totalItems = ref(0)
const itemsPerPage = ref(10)
const page = ref(1)

const selectedPlacementId = ref<string | null>(null)
const selectedPlacementFriendlyName = ref<string>('')
const isLoading = ref(false)
const isTableLoading = ref(false)

const showSnackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('success')

interface IPlacementTableItem {
  id: string
  name: string
  enabled: boolean
  couponCount: number
}

const placementItems = computed((): IPlacementTableItem[] => {
  return Object.entries(props.placements)
    .map(([key, value]) => {
      if (key == null || key === '' || key.startsWith('preview__')) {
        return null
      }

      return {
        id: key,
        name: value.friendlyName ?? '',
        enabled: value.enabled ?? false,
        couponCount: value.coupons?.length ?? 0,
      }
    })
    .filter((item): item is IPlacementTableItem => item != null)
})

interface TableHeader {
  title: string
  key: string
  align: 'start' | 'end' | 'center'
  value: string
}

const tableHeaders: TableHeader[] = [
  {
    title: 'Name',
    key: 'name',
    align: 'start' as const,
    value: 'name',
  },
  {
    title: 'ID',
    key: 'id',
    align: 'start' as const,
    value: 'id',
  },
  {
    title: 'Coupons',
    key: 'couponCount',
    align: 'start' as const,
    value: 'couponCount',
  },
  {
    title: 'Status',
    key: 'enabled',
    align: 'start' as const,
    value: 'enabled',
  },
]

const addNewPlacement = () => {
  if (!newPlacementId.value || placementIdError.value) return

  const newSettings: IPlacements = {
    [newPlacementId.value]: getDefaultPlacementSettings(newPlacementId.value),
  }

  productSettings
    .saveSettings(newSettings)
    .then(() => {
      showSnackbar.value = true
      snackbarText.value = 'New placement added successfully'
      snackbarColor.value = 'success'
    })
    .catch(err => {
      console.error(err)
      showSnackbar.value = true
      snackbarText.value = 'Failed to add new placement'
      snackbarColor.value = 'error'
    })
    .finally(() => {
      newPlacementId.value = ''
      showAddPlacementDialog.value = false
    })
}

const placementIdError = computed(() => {
  if (!newPlacementId.value) {
    return ''
  }

  if (productSettings.placements[newPlacementId.value]) {
    return 'This placement ID already exists'
  }

  if (!/^[a-z\d_-]+$/i.test(newPlacementId.value)) {
    return 'Placement ID can only contain letters, numbers, underscores, and hyphens'
  }

  return ''
})

const handleRowClick = (row: IPlacementTableItem) => {
  selectedPlacementId.value = row.id
  emit('update:placementId', row.id)
}

onMounted(() => {})
</script>

<style scoped>
.data-card {
  height: 135px;
}
.data-list :deep(.v-data-table-header__content) {
  font-weight: bold !important;
}
:deep(.v-skeleton-loader__chip) {
  height: 25px;
  margin: 0 !important;
}
:deep(.v-skeleton-loader__text) {
  height: 19px;
  margin: 0 !important;
}
.data-card__value-title {
  color: #666;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.75;
}
.data-card__value {
  font-size: 2.5em;
  font-weight: bold;
  line-height: 1em;
  margin-top: 2px;
}
.data-card__compared-value {
  color: orange;
}
.data-card__value-skeleton {
  margin-top: 6px;
}
.data-card__stat {
  display: block;
  line-height: 1.25em;
  margin-top: 10px;
}
.data-card__stat-skeleton {
  margin-top: 16px;
}
</style>
